import * as React from 'react';
import Popover, { Align, PopoverInfo, Position } from 'react-tiny-popover';
import { useRef } from 'react';
import { CommonPopupLayout, ICommonPopupLayoutProps } from '../common-popup/common-popup';
import { useClick } from '../../../infrastructure/hooks/use-click';
import { useHover } from '../../../infrastructure/hooks/use-hover';
import { safeUseState } from '../../../infrastructure/hooks/safe-use-state';
import { getPopoverPosition } from '../../../infrastructure/utils/popover.utils';
import { CommonPopoverContext } from './common-popover.context';
import './common-popover.scss';

enum ShowCommonPopoverTrigger {
    Hover,
    Click,
}

interface ICommonPopoverProps extends ICommonPopupLayoutProps {
    align?: Align;
    position?: Position | Position[];
    /**
     * Element have to have 'ref' prop (see ref forwarding in react documentation.)
     * todo: find a way to reflect it in interface
     */
    children: JSX.Element;
    /**
     * can be used in order to avoid manual popover visibility manipulations with 'isOpen' flag
     */
    trigger?: ShowCommonPopoverTrigger;
    /**
     * optional close handler
     */
    onClose?: () => void;
    onClickOutside?: () => void;
    containerClassName?: string;
}

/**
 * IMPORTANT: child of CommonPopover have to forward ref, in order to let popover obtain it's position
 * check out 'ref forwarding' in react documentation.
 *
 * @param props
 * @constructor
 */
const CommonPopover = (props: ICommonPopoverProps) => {
    const [isPopoverOpen, setIsPopoverOpenInner] = safeUseState<boolean>(false);
    const setIsPopoverOpen = (newValue: boolean) => {
        setIsPopoverOpenInner(newValue);
        if (props.onClose) {
            props.onClose();
        }
    };

    // ref is required to be passed into useClick and useHover to let these hooks know about element,
    // which is going to trigger popover visibility
    const ref = useRef(null);

    // click is default trigger
    if (props.trigger === ShowCommonPopoverTrigger.Click || props.trigger == null) {
        const clickHandler = (isClickedWithinElement: boolean) => {
            // if clicked on target element - toggle popover visibility
            if (isClickedWithinElement) {
                setIsPopoverOpen(!isPopoverOpen);
            }
        };
        useClick(clickHandler, ref);
    }

    if (props.trigger === ShowCommonPopoverTrigger.Hover) {
        useHover((isHoveredOnElement: boolean) => {
            if (isHoveredOnElement) {
                setIsPopoverOpen(true);
            } else {
                setIsPopoverOpen(false);
            }
        }, ref);
    }

    let cssClassName = 'oe-popup-layout--popover position--fix';
    if (!isPopoverOpen) {
        cssClassName += ' oe-popup-layout--popover--closed';
    }

    if (props.className) {
        cssClassName += ' ' + props.className;
    }

    return (<CommonPopoverContext.Provider value={{
        isOpen: isPopoverOpen,
        closePopover: () => setIsPopoverOpen(false),
    }}>
        <Popover
            align={props.align}
            position={props.position}
            isOpen={isPopoverOpen}
            onClickOutside={() => {
                setIsPopoverOpen(false);
                if (props.onClickOutside) {
                    props.onClickOutside();
                }
            }}
            contentLocation={(args: PopoverInfo) => getPopoverPosition(ref.current, args)}
            containerClassName={'position--fix react-tiny-popover-container' + (props.containerClassName ? ' ' + props.containerClassName : '')}
            content={
                // we remove content from the popover if it shouldn't be shown due to the bug:
                // when closed, popover is still being rendered with 0 opacity but it still consume space (neither visiblity nor display not set)
                // then both containerClassName and containerStyle accepts only initial value,
                // so we can't control visiblity with them as they are not being changed.
                // therefore the only way to avoid overlapping the body with invisible content - removing this content from popover
                isPopoverOpen && <CommonPopupLayout
                    body={props.body}
                    header={props.header}
                    footer={props.footer}
                    className={cssClassName}
                />}
        >
            {
                // targeted element's type doesn't matter, popover works with any kind of children
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (popoverRef: React.Ref<any>) => {
                // here we ensure that the dom node of element, which activates popover, is passed to popover component's ref (popoverRef)
                // and simultaneously assigned to the ref, used by common-popover for triggering opening/closing of popover
                const childProps = React.isValidElement(props.children) ? props.children.props as Record<string, unknown> : null;
                const existingOnFocus = childProps && typeof childProps.onFocus === 'function'
                    ? (childProps.onFocus as (e: React.FocusEvent) => void)
                    : undefined;
                return React.cloneElement(props.children, {
                    ref: node => {
                        if (typeof popoverRef === 'function') {
                            popoverRef(node);
                        } else if (popoverRef != null) {
                            // popoverRef is defined by Popover component and utilized to calculate it's position
                            // we are eligible to assign it here even though commonly it shall not be assigned explicitly outside of react
                            // @ts-ignore
                            popoverRef.current = node;
                        }
                        ref.current = node;
                    },
                    onFocus: (e: React.FocusEvent) => {
                        if (props.trigger !== ShowCommonPopoverTrigger.Hover && !isPopoverOpen) {
                            setIsPopoverOpen(true);
                        }
                        existingOnFocus?.(e);
                    },
                });
            }}
        </Popover>
    </CommonPopoverContext.Provider>);
};

export { CommonPopover };